import Box from "@mui/material/Box";
import WizardStep from "../../../../components/Wizard/WizardStep"
import useICPWizard from "../hooks/useICPWizard";
import { ReactNode, useMemo, useState } from "react";
import Typography from "@mui/material/Typography";
import { Sprint } from "../../../../stores/SprintStore";
import { Alert, Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, Switch, TextField } from "@mui/material";
import ErrorLabel from "../../../../components/ErrorLabel";
import CustomDatePicker from "../../../common/CustomDatePicker";
import useOptions from "../../../../hooks/useOptions";

type Props = {    
    onCancel: () => void,
    onSaved: (loadAssignLeads: boolean) => void,
    steps: ReactNode
}

const ICPDetails = ({ onCancel, steps, onSaved }: Props) => {
    const { errors, handleNextStep,handlePreviousStep, icp, onICPChange, icpIsValid, saveICP, sprint } = useICPWizard();
    const [showError, setShowError] = useState(false);
    const [saving, setSaving] = useState(false);
    const sprintParams = useMemo(() => { return { params: { playbookId: sprint.playbookId } }; }, [sprint.playbookId]);
    const { options: cadences } = useOptions('PLAYBOOKCADENCESBYPLAYBOOK', sprintParams);
    const { options: owners } = useOptions('OWNER');    

    const handleSave = async () => {
        setShowError(false);

        try {
            if (icpIsValid()) {
                setSaving(true);
                await saveICP(icp);
                handleNextStep();
            }
        } catch (err) {
            setShowError(true);
        }

        setSaving(false);
    }

    return (
        <WizardStep
            title="ICP Builder - Name & Options"
            subtitle="Ideal Customer Profile"
            onSave={handleSave}
            onPrevious={handlePreviousStep}
            onCancel={onCancel}
            isSaving={saving}

        >
            <Box display='flex' sx={{ height: '100%' }}>
                {steps}
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', rowGap: 4, width: '100%' }}>
                    <Box display='flex' flexDirection='column' rowGap={1}>
                        {showError && <Alert severity="error">There was an error saving the ICP</Alert>}                        
                        <Typography variant='body2'>Please enter a name for the ideal customer profile and click save.</Typography>
                    </Box>
                    <FormControl fullWidth sx={{ maxWidth: 650 }} size="small">
                        <TextField size="small" error={errors.some(s => s.propertyName === 'name')} label="Profile Name" value={icp.name} onChange={(e) => onICPChange({ ...icp, name: e.target.value })} sx={{ maxWidth: 650 }} />
                        <ErrorLabel error={errors.find(s => s.propertyName === 'name')} />
                    </FormControl>

                    <FormControl fullWidth sx={{ maxWidth: 650 }} size="small">
                        <InputLabel id="cadence-label">Cadence</InputLabel>
                        <Select
                            labelId="cadence-label"
                            id="cadence"
                            label="Cadence"
                            value={icp.cadenceId}
                            error={errors.some(s => s.propertyName === 'cadenceId')}
                            onChange={(e) => onICPChange({ ...icp, cadenceId: e.target.value })}
                        >
                            {cadences.map(p => <MenuItem key={p.value} value={p.value}>{p.name}</MenuItem>)}
                        </Select>
                        <ErrorLabel error={errors.find(s => s.propertyName === 'cadenceId')} />
                    </FormControl>

                    <Box width={240}>
                        <CustomDatePicker value={icp.cadenceStartDate} onChange={(newDate: string) => { onICPChange({ ...icp, cadenceStartDate: newDate }) }} label="Cadence Start Date" />
                        <ErrorLabel error={errors.find(s => s.propertyName === 'cadenceStartDate')} />
                    </Box>
                    <FormControl fullWidth sx={{ maxWidth: 240 }} size="small">
                        <InputLabel id="lead-owner-label">Lead Owner (optional)</InputLabel>
                        <Select
                            labelId="lead-owner-label"
                            id="lead-owner"
                            label="Lead Owner (optional)"
                            value={icp.ownerId}
                            onChange={(e) => onICPChange({ ...icp, ownerId: e.target.value })}
                        >
                            <MenuItem value={'00000000-0000-0000-0000-000000000000'}>None</MenuItem>
                            {owners.map(p => <MenuItem key={p.value} value={p.value}>{p.name}</MenuItem>)}
                        </Select>
                    </FormControl>

                    <FormGroup>                        
                        <FormControlLabel disabled control={<Switch />} label="Automatically Assign Leads" />
                    </FormGroup>
                </Box>
            </Box>
        </WizardStep>
    )
}

export default ICPDetails;